import * as React from "react"

import Icons from "@src/components/Icons"
import Stack from "@mui/material/Stack"
import Popover from "@mui/material/Popover"
import MenuItem from "@mui/material/MenuItem"
import usePopover from "@src/hooks/use-popover"
import { styled } from "@mui/system"
import { getClassNameActive } from "../config"
import { useTranslation } from "react-i18next"

const HOVER_CLOSE_DELAY_MS = 150

const MenuItemCustom = styled(MenuItem)(({ theme }) => ({
  color: "#141414 !important",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "24px",
  padding: "0 16px",
  minHeight: "48px !important",
  minWidth: 200,
  borderBottom: "1px solid #E5E5E5",
  textTransform: "uppercase",
  "&:hover": {
    color: "#000 !important",
    backgroundColor: "#F6F6F6 !important"
  },
  "&.active": {
    color: "#000 !important",
    backgroundColor: "#F6F6F6 !important"
  }
}))

const SubMenuItem = styled(MenuItem)(({ theme }) => ({
  color: "#141414 !important",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "24px",
  padding: "0 16px",
  minHeight: "48px !important",
  minWidth: 200,
  borderBottom: "1px solid #E5E5E5",
  textTransform: "uppercase",
  position: "relative",
  "&:hover": {
    color: "#000 !important",
    backgroundColor: "#F6F6F6 !important"
  },
  "&.active": {
    color: "#000 !important",
    backgroundColor: "#F6F6F6 !important"
  }
}))

const useDelayedPopover = () => {
  const popoverHook = usePopover()
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearCloseTimer = React.useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const handleOpen = React.useCallback(() => {
    clearCloseTimer()
    popoverHook.handleOpen()
  }, [clearCloseTimer, popoverHook])

  const handleClose = React.useCallback(() => {
    clearCloseTimer()
    closeTimerRef.current = setTimeout(() => {
      popoverHook.handleClose()
    }, HOVER_CLOSE_DELAY_MS)
  }, [clearCloseTimer, popoverHook])

  React.useEffect(() => () => clearCloseTimer(), [clearCloseTimer])

  return {
    ...popoverHook,
    handleOpen,
    handleClose,
  }
}

const MenuWithSubItem = props => {
  const { t } = useTranslation()
  const { currentPath, data, onRedirect } = props || { data: {} }
  const popoverHook = useDelayedPopover()

  const handleParentClick = () => {
    if (data.path) {
      onRedirect(data.path)()
      popoverHook.handleClose()
    }
  }

  return (
    <>
      <div
        style={{
          height: "100%"
        }}
        ref={popoverHook.anchorRef}
      >
        <Stack
          sx={{
            display: "flex",
            whiteSpace: "nowrap",
            backgroundColor: "transparent",
            border: "none",
            padding: "0",
            minHeight: "auto ",
            height: "100%",
            outline: 0,
            cursor: "pointer",
            fontSize: 16,
            color: "#434343",
            "&:hover": {
              color: "#000 !important",
              fontWeight: 700
            },
            "&.active": {
              color: "#000 !important",
              fontWeight: 700
            }
          }}
          className={
            popoverHook?.open || getClassNameActive(data, currentPath)
              ? "active"
              : ""
          }
          role="button"
          onClick={handleParentClick}
          onMouseEnter={popoverHook.handleOpen}
          onMouseLeave={popoverHook.handleClose}
        >
          {t(data.key)}
          <Icons
            name="arrow-up"
            sx={{
              marginLeft: "2px",
              transition: "transform 0.15s",
              transform: popoverHook?.open ? "rotate(0deg)" : "rotate(180deg)"
            }}
          />
        </Stack>
      </div>
      <Popover
        open={popoverHook.open}
        anchorEl={popoverHook.anchorRef.current}
        onMouseEnter={popoverHook.handleOpen}
        onMouseLeave={popoverHook.handleClose}
        slotProps={{ paper: { sx: { pointerEvents: "auto" } } }}
        anchorOrigin={{
          horizontal: "left",
          vertical: "bottom"
        }}
        sx={{
          pointerEvents: "none",
          "& .MuiPaper-root": {
            borderRadius: "4px"
          }
        }}
      >
        {data.subMenu.map((item, index) => (
          <SubMenuItemWrapper
            key={index}
            item={item}
            currentPath={currentPath}
            onRedirect={onRedirect}
          />
        ))}
      </Popover>
    </>
  )
}

const SubMenuItemWrapper = ({ item, currentPath, onRedirect }) => {
  const { t } = useTranslation()
  const subPopoverHook = useDelayedPopover()

  const handleClickItem = path => () => {
    onRedirect(path)()
    subPopoverHook.handleClose()
  }

  if (item.subMenu && item.subMenu.length > 0) {
    return (
      <div key={item.key}>
        <SubMenuItem
          ref={subPopoverHook.anchorRef}
          onMouseEnter={subPopoverHook.handleOpen}
          onMouseLeave={subPopoverHook.handleClose}
          onClick={e => {
            e.stopPropagation()
            if (item.path) {
              onRedirect(item.path)()
              subPopoverHook.handleClose()
              return
            }
            subPopoverHook.handleOpen()
          }}
          className={getClassNameActive(item, currentPath)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          {item.key ? t(item.key) : item.title}
          <Icons
            name="arrow-down"
            sx={{
              marginLeft: "8px",
              fontSize: "16px"
            }}
          />
        </SubMenuItem>
        <Popover
          open={subPopoverHook.open}
          anchorEl={subPopoverHook.anchorRef.current}
          onMouseEnter={subPopoverHook.handleOpen}
          onMouseLeave={subPopoverHook.handleClose}
          slotProps={{ paper: { sx: { pointerEvents: "auto" } } }}
          anchorOrigin={{
            horizontal: "right",
            vertical: "top"
          }}
          transformOrigin={{
            horizontal: "left",
            vertical: "top"
          }}
          sx={{
            pointerEvents: "none",
            "& .MuiPaper-root": {
              borderRadius: "4px"
            }
          }}
        >
          {item.subMenu.map((subItem, subIndex) => (
            <MenuItemCustom
              key={subIndex}
              onClick={handleClickItem(subItem.path)}
              className={getClassNameActive(subItem, currentPath)}
            >
              {subItem.key ? t(subItem.key) : subItem.title}
            </MenuItemCustom>
          ))}
        </Popover>
      </div>
    )
  }

  return (
    <MenuItemCustom
      onClick={handleClickItem(item.path)}
      className={getClassNameActive(item, currentPath)}
    >
      {item.key ? t(item.key) : item.title}
    </MenuItemCustom>
  )
}

export default MenuWithSubItem
